import type { Product } from "@tellescope/types-models";
import type { CreateFields } from "@tellescope/types-client";
import { Label } from "../../label";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";
import { Textarea } from "../../textarea";
import { CurrencyInput } from "../../CurrencyInput";
import { DateTimePicker } from "../../DateTimePicker";
import { Button } from "../../button";

interface LogPurchaseContentProps {
  enduserId: string;
  products: Product[];
  onSubmit: (purchase: CreateFields<"purchases">) => void;
  onCancel: () => void;
}

export function LogPurchaseContent({ enduserId, products, onSubmit, onCancel }: LogPurchaseContentProps) {
  const { t } = useTranslation("payments");
  const [formData, setFormData] = useState<Partial<CreateFields<"purchases">>>({
    title: "New Purchase",
    processor: "Stripe",
    enduserId,
  });
  const selectedProduct = useMemo(
    () => products.find(({ id }) => id === formData.productId),
    [formData.productId, products]
  );

  const formIsComplete = useMemo(
    () => !!formData.productId && formData.cost?.amount !== undefined && !!formData.processedAt,
    [formData.productId, formData.cost?.amount, formData.processedAt]
  );

  return (
    <form
      className="flex flex-col h-full overflow-y-auto p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!formData.cost) {
          // TODO: add validation UI state change when we have `useForm` hook
          return;
        }
        onSubmit({
          ...formData,
          title: "New Purchase",
          cost: formData.cost,
          processor: selectedProduct?.processor ?? "Stripe",
          enduserId,
        });
      }}
    >
      <div className="flex-1 grow-1 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 p-1">
            <Label htmlFor="product">{t("Select Product")}</Label>
            <Select
              value={formData.productId}
              onValueChange={(productId) => {
                const newProduct = products.find(({ id }) => id === productId);
                setFormData((prev) => ({
                  ...prev,
                  productId,
                  cost: newProduct?.cost ?? prev.cost,
                }));
              }}
              required
            >
              <SelectTrigger id="product" className="w-full">
                <SelectValue placeholder={t("Select Product")} />
              </SelectTrigger>
              <SelectContent>
                {products.map(({ id, title }) => (
                  <SelectItem key={id} value={id}>
                    {title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 p-1">
            <Label htmlFor="cost" className={!formData.productId ? "text-muted-foreground" : undefined}>
              {/* TODO: support other currencies */}
              {t("Cost in Dollars ($)")}
            </Label>
            <CurrencyInput
              id="cost"
              value={formData.cost?.amount ?? 0}
              disabled={!formData.productId}
              onChange={(amount) => {
                setFormData((prev) => ({
                  ...prev,
                  cost: {
                    currency: prev.cost?.currency ?? "USD",
                    amount,
                  },
                }));
              }}
            />
          </div>
          <DateTimePicker
            id="processedAt"
            className="space-y-2 p-1"
            label={t("Purchased At")}
            value={formData.processedAt || undefined}
            placeholder={t("Select a Date & Time")}
            onChange={(processedAt) => {
              setFormData((prev) => ({ ...prev, processedAt }));
            }}
          />
          <div className="space-y-2 p-1">
            <Label htmlFor="title">{t("Add Notes")}</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, notes: e.target.value }));
              }}
            />
          </div>
        </div>
      </div>
      <div className="w-full flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!formIsComplete} type="submit">
          {t("Save")}
        </Button>
      </div>
    </form>
  );
}
